import {
  PipelineDataType,
  PipelineDimension,
  PipelineOverTimeDataPoint,
  PipelinePeriod,
  PipelineValueOverTimeResponse,
  TransformedChartData
} from "@/components/layouts/PipelineOverTime/types";
import { formatNumber } from "@/utils/numbers";

export const getQuarterFromMonth = (month: number): number => {
  return Math.ceil(month / 3);
};

export const transformPipelineData = (
  dataPoints: PipelineOverTimeDataPoint[],
  period: PipelinePeriod = PipelinePeriod.MONTHLY
): TransformedChartData[] => {
  const allYears = dataPoints.map((point) => point.year);
  const uniqueYears = new Set(allYears);
  const isSingleYear = uniqueYears.size === 1;

  return dataPoints.map((point) => {
    const isForecast = point.dataType === PipelineDataType.FORECAST;

    const quarter = getQuarterFromMonth(point.month);
    const dateLabel =
      period === PipelinePeriod.QUARTERLY
        ? isSingleYear
          ? `Q${quarter}`
          : `Q${quarter} ${point.year}`
        : `${point.month}/${point.year.toString().slice(-2)}`;

    const isCurrent =
      period === PipelinePeriod.QUARTERLY
        ? isCurrentQuarter(quarter, point.year)
        : isCurrentPeriod(point.month, point.year);

    return {
      date: dateLabel,
      month: point.month,
      year: point.year,
      quarter,
      actualPipeline: isForecast ? 0 : point.pipeline.value,
      forecastedPipeline: isForecast ? point.pipeline.value : 0,
      actualDeals: isForecast ? 0 : point.pipeline.dealsCount,
      forecastedDeals: isForecast ? point.pipeline.dealsCount : 0,
      isCurrent,
      periodEndDate: point.periodEndDate
    };
  });
};

export const formatPipelineValue = (value: number): string => {
  const formatted = formatNumber(value, 2, true);
  return `${formatted.currencySign}${formatted.value}${formatted.modifier}`;
};

export const getCurrentPeriod = (): { month: number; year: number } => {
  const now = new Date();
  return {
    month: now.getMonth() + 1,
    year: now.getFullYear()
  };
};

export const isCurrentPeriod = (month: number, year: number): boolean => {
  const current = getCurrentPeriod();
  return current.month === month && current.year === year;
};

export const isCurrentQuarter = (quarter: number, year: number): boolean => {
  const current = getCurrentPeriod();
  const currentQuarter = getQuarterFromMonth(current.month);
  return current.year === year && currentQuarter === quarter;
};

export const transformApiResponseToComponentData = (
  apiResponse: PipelineValueOverTimeResponse
): PipelineOverTimeDataPoint[] => {
  const dataPoints: PipelineOverTimeDataPoint[] = [];
  const isQuarterly = apiResponse.dimension === PipelineDimension.QUARTER;

  Object.entries(apiResponse.byDimension).forEach(([dateKey, data]) => {
    let month: number;
    let year: number;

    if (isQuarterly) {
      // Format: "2025-Q2"
      const [yearStr, quarterStr] = dateKey.split("-Q");
      year = parseInt(yearStr, 10);
      const quarter = parseInt(quarterStr, 10);
      // Use first month of quarter
      month = (quarter - 1) * 3 + 1;
    } else {
      // Format: "2025-04"
      const [yearStr, monthStr] = dateKey.split("-");
      year = parseInt(yearStr, 10);
      month = parseInt(monthStr, 10);
    }

    dataPoints.push({
      month,
      year,
      pipeline: {
        value: data.all.value,
        dealsCount: data.all.dealCount
      },
      dataType: data.all.dataType,
      periodEndDate: data.all.periodEndDate
    });
  });

  dataPoints.sort((a, b) => {
    if (a.year !== b.year) {
      return a.year - b.year;
    }
    return a.month - b.month;
  });

  return dataPoints;
};
